import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable, type Column } from '../components/table'
import type {
  CapacityDatastore,
  CapacityDevice,
  CapacityHost,
  CapacityStorageHistory,
} from '../api/types'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Capacity plan: capacità e utilizzo delle risorse, dai dati già in inventario.
 *
 * Tre dimensioni, tre domande: **storage** (quanto sono pieni i datastore),
 * **compute** (quante VM stanno ancora su questi host — l'overcommit), **porte**
 * (quanto spazio resta per collegare). Nessuna raccolta nuova: legge ciò che la
 * discovery ha già visto. La % di banda delle porte e le proiezioni nel tempo
 * verranno quando ci sarà una serie temporale — qui c'è la fotografia, che è già
 * ciò che serve per decidere se comprare.
 */
export default function CapacityPlan() {
  const dati = useAsync(() => api.capacity(), [])
  const storia = useAsync(() => api.capacityStorageHistory(), [])

  if (dati.loading && !dati.data) return <Spinner />

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Capacity plan')}</h1>
          <p className="page__subtitle">
            {t('mostra capacità, spazio utilizzato e disponibilità delle risorse di storage — e di compute e porte — rilevate nell’inventario, per individuare saturazioni e capacità residua.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={dati.error} />

      {dati.data && (
        <>
          <SezioneStorage storage={dati.data.storage} storia={storia.data ?? null} />
          <SezioneCompute compute={dati.data.compute} />
          <SezionePorte ports={dati.data.ports} />
        </>
      )}
    </>
  )
}

// ------------------------------------------------------------------ storage

function SezioneStorage({
  storage,
  storia,
}: {
  storage: import('../api/types').CapacityReport['storage']
  storia: CapacityStorageHistory | null
}) {
  const tot = storage.total
  return (
    <Card title={t('Storage')}>
      {storage.datastores.length === 0 ? (
        <EmptyState
          title={t('Nessun datastore')}
          hint={t('Compaiono qui i datastore raccolti dai connettori di virtualizzazione.')}
        />
      ) : (
        <>
          <div className="cap-tot">
            <Totale label={t('Capacità totale')} value={formatBytes(tot.capacity_bytes)} />
            <Totale label={t('Usato')} value={`${formatBytes(tot.used_bytes)} · ${pct(tot.used_percent)}`} />
            <Totale label={t('Libero')} value={formatBytes(tot.free_bytes)} />
            {storia?.daily_growth_bytes != null && (
              <Totale label={t('Crescita al giorno')}
                      value={formatGrowth(storia.daily_growth_bytes)} />
            )}
            {storia?.days_to_full != null && (
              <Totale label={t('Pieno fra')}
                      value={t('~{n} giorni', { n: storia.days_to_full })}
                      warn={storia.days_to_full <= 90} />
            )}
            <Totale label={t('Datastore da tenere d’occhio')} value={String(tot.warnings)}
                    warn={tot.warnings > 0} />
          </div>
          {storia && <GraficoAndamento storia={storia} />}
          <DataTable
            rows={storage.datastores}
            rowKey={(d) => d.id}
            initialSort={{ key: 'used_percent', desc: true }}
            columns={colonneStorage()}
          />
        </>
      )}
    </Card>
  )
}

const colonneStorage = (): Column<CapacityDatastore>[] => [
  {
    key: 'name', header: t('Storage'), value: (d) => d.name,
    render: (d) => (
      <>
        <Link to={`/cis/${d.id}`}>{d.name}</Link>
        {d.kind === 'storage_system' && (
          // Un array pesa diverso da un datastore: si dice.
          <span className="badge" style={{ marginLeft: 6 }}>{t('array')}</span>
        )}
      </>
    ),
  },
  {
    key: 'source', header: t('Ambiente'), value: (d) => d.source || '',
    render: (d) => <span className="muted">{d.source || '—'}</span>,
  },
  {
    key: 'hosts_served', header: t('Host serviti'), type: 'number', align: 'right',
    value: (d) => d.hosts_served ?? -1,
    // Il numero che trasforma «si riempie» in «chi si ferma»: solo sugli
    // array, dove le relazioni stored_on lo dicono.
    render: (d) => d.hosts_served != null
      ? String(d.hosts_served)
      : <span className="muted">—</span>,
  },
  {
    key: 'capacity_bytes', header: t('Capacità'), type: 'number', align: 'right',
    value: (d) => d.capacity_bytes ?? 0, render: (d) => formatBytes(d.capacity_bytes),
  },
  {
    // Il buffer: la domanda con cui si apre il capacity plan («posso metterci
    // altro?»), che la percentuale da sola non dice.
    key: 'free_bytes', header: t('Libero'), type: 'number', align: 'right',
    value: (d) => d.free_bytes ?? -1,
    render: (d) => d.free_bytes != null
      ? <>{formatBytes(d.free_bytes)}</>
      : <span className="muted">—</span>,
  },
  {
    key: 'used_percent', header: t('Utilizzo'), type: 'number',
    value: (d) => d.used_percent ?? 0,
    render: (d) => <Barra percent={d.used_percent} warn={d.warning} />,
  },
  {
    key: 'daily_growth_bytes', header: t('Crescita/giorno'), type: 'number', align: 'right',
    value: (d) => d.daily_growth_bytes ?? -1,
    render: (d) => d.daily_growth_bytes != null
      ? <>{formatGrowth(d.daily_growth_bytes)}</>
      : <span className="muted">—</span>,
  },
  {
    key: 'days_to_full', header: t('Pieno fra'), type: 'number', align: 'right',
    value: (d) => d.days_to_full ?? Number.MAX_SAFE_INTEGER,
    render: (d) => d.days_to_full != null
      ? <span className={d.days_to_full <= 90 ? 'cap-scadenza--warn' : ''}>{t('~{n} giorni', { n: d.days_to_full })}</span>
      : <span className="muted">—</span>,
  },
]

// ------------------------------------------- il grafico andamento + proiezione

const GW = 920
const GH = 240
const GPAD = { top: 12, right: 12, bottom: 26, left: 64 }

/**
 * L'andamento dello storage usato, con la capacità come tetto e la proiezione
 * come tratteggio: la retta della crescita stimata, prolungata fino al pieno
 * (o all'orizzonte del grafico, se il pieno è oltre).
 *
 * Il grafico si riempie da solo, un punto al giorno: nei primi giorni mostra
 * pochi punti e nessuna proiezione — un tratteggio su due punti sarebbe una
 * bugia disegnata bene.
 */
function GraficoAndamento({ storia }: { storia: CapacityStorageHistory }) {
  const dati = useMemo(() => preparaGrafico(storia), [storia])

  if (!dati) {
    return (
      <p className="field__hint">
        {t('La serie storica si sta riempiendo: un campione al giorno per datastore. Il grafico compare con i primi punti, la proiezione dopo una settimana.')}
      </p>
    )
  }

  const { punti, proiezione, capacita, yMax, giorniTotali, t0, etichetteY, etichetteX, pienoX } = dati

  const x = (g: number) => GPAD.left + (g / giorniTotali) * (GW - GPAD.left - GPAD.right)
  const y = (v: number) => GPAD.top + (1 - v / yMax) * (GH - GPAD.top - GPAD.bottom)

  const lineaUsato = punti.map((p, i) => `${i ? 'L' : 'M'}${x(p.g)},${y(p.v)}`).join(' ')
  const area = `${lineaUsato} L${x(punti[punti.length - 1].g)},${y(0)} L${x(punti[0].g)},${y(0)} Z`
  const lineaCap = capacita.map((p, i) => `${i ? 'L' : 'M'}${x(p.g)},${y(p.v)}`).join(' ')

  return (
    <div className="cap-grafico">
      <svg viewBox={`0 0 ${GW} ${GH}`} className="cap-grafico__svg" role="img"
           aria-label={t('Andamento dello storage usato con proiezione')}>
        {/* griglia e assi */}
        {etichetteY.map((e) => (
          <g key={e.v}>
            <line x1={GPAD.left} x2={GW - GPAD.right} y1={y(e.v)} y2={y(e.v)}
                  className="cap-grafico__griglia" />
            <text x={GPAD.left - 8} y={y(e.v) + 4} className="cap-grafico__tick" textAnchor="end">
              {e.label}
            </text>
          </g>
        ))}
        {etichetteX.map((e) => (
          <text key={e.g} x={x(e.g)} y={GH - 8} className="cap-grafico__tick" textAnchor="middle">
            {e.label}
          </text>
        ))}

        {/* capacità: il tetto */}
        <path d={lineaCap} className="cap-grafico__capacita" />

        {/* usato: area + linea */}
        <path d={area} className="cap-grafico__area" />
        <path d={lineaUsato} className="cap-grafico__usato" />

        {/* proiezione: il tratteggio della crescita stimata */}
        {proiezione && (
          <path
            d={proiezione.map((p, i) => `${i ? 'L' : 'M'}${x(p.g)},${y(p.v)}`).join(' ')}
            className="cap-grafico__proiezione"
          />
        )}
        {/* il giorno del pieno, se cade nell'orizzonte */}
        {pienoX != null && (
          <g>
            <line x1={x(pienoX)} x2={x(pienoX)} y1={GPAD.top} y2={GH - GPAD.bottom}
                  className="cap-grafico__pieno" />
            <text x={x(pienoX)} y={GPAD.top + 12} className="cap-grafico__pieno-testo"
                  textAnchor={pienoX > giorniTotali * 0.85 ? 'end' : 'start'} dx={pienoX > giorniTotali * 0.85 ? -6 : 6}>
              {t('pieno · {d}', { d: dataFraGiorni(t0, pienoX) })}
            </text>
          </g>
        )}
      </svg>
      <div className="cap-grafico__legenda">
        <span className="cap-grafico__voce"><span className="cap-grafico__campione cap-grafico__campione--usato" />{t('Usato')}</span>
        <span className="cap-grafico__voce"><span className="cap-grafico__campione cap-grafico__campione--capacita" />{t('Capacità')}</span>
        {proiezione && (
          <span className="cap-grafico__voce"><span className="cap-grafico__campione cap-grafico__campione--proiezione" />{t('Proiezione')}</span>
        )}
      </div>
    </div>
  )
}

type PuntoG = { g: number; v: number }

function preparaGrafico(storia: CapacityStorageHistory): {
  punti: PuntoG[]
  capacita: PuntoG[]
  proiezione: PuntoG[] | null
  yMax: number
  giorniTotali: number
  t0: number
  etichetteY: { v: number; label: string }[]
  etichetteX: { g: number; label: string }[]
  pienoX: number | null
} | null {
  const s = storia.series
  if (s.length < 2) return null

  const t0 = Date.parse(s[0].date)
  const giorno = (iso: string) => Math.round((Date.parse(iso) - t0) / 86_400_000)
  const punti = s.map((p) => ({ g: giorno(p.date), v: p.used_bytes }))
  const capacita = s.map((p) => ({ g: giorno(p.date), v: p.capacity_bytes }))

  const ultimoG = punti[punti.length - 1].g
  const ultimoV = punti[punti.length - 1].v
  const capUlt = capacita[capacita.length - 1].v

  // L'orizzonte: la storia più un futuro proporzionato — fino al pieno se è
  // vicino, altrimenti metà della finestra osservata (una proiezione lunga il
  // triplo della storia sarebbe quasi tutta inchiostro inventato).
  const crescita = storia.daily_growth_bytes
  const pieno = storia.days_to_full
  let orizzonte = Math.max(7, Math.round(ultimoG * 0.5))
  if (pieno != null) orizzonte = Math.min(Math.max(pieno + 5, 7), Math.max(ultimoG, 30) * 2)

  let proiezione: PuntoG[] | null = null
  let pienoX: number | null = null
  if (crescita != null && punti.length >= 7) {
    const fine = ultimoG + orizzonte
    proiezione = [
      { g: ultimoG, v: ultimoV },
      { g: fine, v: Math.min(ultimoV + crescita * orizzonte, capUlt * 1.02) },
    ]
    if (pieno != null && pieno <= orizzonte) pienoX = ultimoG + pieno
    // Il tetto continua piatto sotto la proiezione, così il tratteggio ha
    // qualcosa contro cui leggersi.
    capacita.push({ g: fine, v: capUlt })
  }

  const giorniTotali = Math.max(capacita[capacita.length - 1].g, ultimoG, 1)
  const yMax = Math.max(...capacita.map((p) => p.v), ...punti.map((p) => p.v)) * 1.08

  const etichetteY = [0.25, 0.5, 0.75, 1].map((f) => {
    const v = yMax * f
    return { v, label: formatBytes(v) ?? '' }
  })
  const etichetteX: { g: number; label: string }[] = []
  const passo = Math.max(1, Math.round(giorniTotali / 6))
  for (let g = 0; g <= giorniTotali; g += passo) {
    etichetteX.push({ g, label: dataFraGiorni(t0, g) })
  }

  return { punti, capacita, proiezione, yMax, giorniTotali, t0, etichetteY, etichetteX, pienoX }
}

function dataFraGiorni(t0: number, giorni: number): string {
  const d = new Date(t0 + giorni * 86_400_000)
  return d.toLocaleDateString(localeCorrente(), { day: 'numeric', month: 'short' })
}

/** «+1,2 GB» o «−300 MB»: la crescita col segno, perché il segno è la notizia. */
function formatGrowth(bytes: number): string {
  // Sotto il MB al giorno la crescita è ferma per ogni decisione di capacity.
  if (Math.abs(bytes) < 1024 ** 2) return '≈0'
  const segno = bytes >= 0 ? '+' : '−'
  return `${segno}${formatBytes(Math.abs(bytes))}`
}

// ------------------------------------------------------------------ compute

function SezioneCompute({ compute }: { compute: import('../api/types').CapacityReport['compute'] }) {
  const tot = compute.total
  return (
    <Card title={t('Compute (overcommit)')}>
      {compute.hosts.length === 0 ? (
        <EmptyState
          title={t('Nessun host')}
          hint={t('Compaiono qui gli host raccolti dai connettori di virtualizzazione.')}
        />
      ) : (
        <>
          <div className="cap-tot">
            <Totale label={t('Core fisici')} value={String(tot.cpu_cores ?? 0)} />
            <Totale label={t('vCPU allocate')} value={String(tot.vcpu_allocated ?? 0)} />
            <Totale label={t('Overcommit CPU')} value={`${tot.cpu_overcommit ?? '—'}×`}
                    warn={(tot.cpu_overcommit ?? 0) > 3} />
            <Totale label={t('Overcommit RAM')} value={`${tot.memory_overcommit ?? '—'}×`}
                    warn={(tot.memory_overcommit ?? 0) > 1.5} />
          </div>
          <p className="field__hint">
            {t('L’overcommit è il rapporto fra risorse promesse alle VM e risorse fisiche: sopra 1× si conta sul fatto che non tutte tirino insieme.')}
          </p>
          <DataTable
            rows={compute.hosts}
            rowKey={(h) => h.id}
            initialSort={{ key: 'cpu_overcommit', desc: true }}
            columns={colonneCompute()}
          />
        </>
      )}
    </Card>
  )
}

const colonneCompute = (): Column<CapacityHost>[] => [
  {
    key: 'name', header: t('Host'), value: (h) => h.name,
    render: (h) => <><Link to={`/cis/${h.id}`}>{h.name}</Link>{h.cluster && <span className="muted"> · {h.cluster}</span>}</>,
  },
  {
    key: 'vm_count', header: t('VM'), type: 'number', align: 'right', value: (h) => h.vm_count,
  },
  {
    key: 'cpu_overcommit', header: t('CPU (vCPU / core)'), type: 'number', align: 'right',
    value: (h) => h.cpu_overcommit ?? 0,
    render: (h) => (
      <>
        {h.vcpu_allocated} / {h.cpu_cores ?? '—'}
        {h.cpu_overcommit != null && (
          <span className={`cap-oc${h.cpu_overcommit > 3 ? ' cap-oc--warn' : ''}`}> {h.cpu_overcommit}×</span>
        )}
      </>
    ),
  },
  {
    key: 'memory_overcommit', header: t('RAM (alloc. / fisica)'), type: 'number', align: 'right',
    value: (h) => h.memory_overcommit ?? 0,
    render: (h) => (
      <>
        {formatBytes(h.vm_memory_allocated)} / {formatBytes(h.memory_bytes)}
        {h.memory_overcommit != null && (
          <span className={`cap-oc${h.memory_overcommit > 1.5 ? ' cap-oc--warn' : ''}`}> {h.memory_overcommit}×</span>
        )}
      </>
    ),
  },
]

// -------------------------------------------------------------------- porte

function SezionePorte({ ports }: { ports: import('../api/types').CapacityReport['ports'] }) {
  const tot = ports.total
  return (
    <Card title={t('Porte degli apparati')}>
      {ports.devices.length === 0 ? (
        <EmptyState
          title={t('Nessun apparato con porte note')}
          hint={t('Le porte si raccolgono con una discovery SNMP approfondita (deep).')}
        />
      ) : (
        <>
          <div className="cap-tot">
            <Totale label={t('Porte totali')} value={String(tot.ports)} />
            <Totale label={t('In uso')} value={String(tot.used)} />
            <Totale label={t('Libere')} value={String(tot.free)} />
          </div>
          <DataTable
            rows={ports.devices}
            rowKey={(d) => d.id}
            initialSort={{ key: 'free', desc: false }}
            columns={colonnePorte()}
          />
        </>
      )}
    </Card>
  )
}

const colonnePorte = (): Column<CapacityDevice>[] => [
  {
    key: 'name', header: t('Apparato'), value: (d) => d.name,
    render: (d) => (
      <>
        <Link to={`/cis/${d.id}`}>{d.name}</Link>
        {d.mgmt_ip && <span className="muted mono"> · {d.mgmt_ip}</span>}
      </>
    ),
  },
  { key: 'free', header: t('Libere'), type: 'number', align: 'right', value: (d) => d.free },
  {
    key: 'used', header: t('In uso / totali'), type: 'number', align: 'right',
    value: (d) => d.used, render: (d) => <>{d.used} / {d.total}</>,
  },
  {
    key: 'used_percent', header: t('Utilizzo'), type: 'number',
    value: (d) => d.used_percent ?? 0,
    render: (d) => <Barra percent={d.used_percent} warn={d.free <= 2} />,
  },
]

// ------------------------------------------------------------------ pezzi

function Totale({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="cap-tot__voce">
      <span className={`cap-tot__valore${warn ? ' cap-tot__valore--warn' : ''}`}>{value}</span>
      <span className="cap-tot__label">{label}</span>
    </div>
  )
}

function Barra({ percent, warn }: { percent: number | null; warn?: boolean }) {
  if (percent == null) return <span className="muted">{t('n/d')}</span>
  const p = Math.max(0, Math.min(100, percent))
  return (
    <div className="cap-barra" title={`${Math.round(percent)}%`}>
      <div className={`cap-barra__fill${warn || p >= 85 ? ' cap-barra__fill--warn' : ''}`}
           style={{ width: `${p}%` }} />
      <span className="cap-barra__num">{Math.round(percent)}%</span>
    </div>
  )
}

function pct(v: number | null): string {
  return v == null ? t('n/d') : `${Math.round(v)}%`
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return '—'
  const tb = bytes / 1024 ** 4
  if (tb >= 1) return `${tb.toFixed(tb >= 10 ? 0 : 1)} TB`
  const gb = bytes / 1024 ** 3
  return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`
}
